import { useEffect, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { API_BASE_URL, authFetch } from '@/services/api';

// Dispatcher — PLAN vs FACT generation (MWh) over a period. Reuses the live
// /owner/revenue-series endpoint, which already returns planMwh/factMwh per day.
// Purely technical: no money, only energy.
const PLAN = '#7FB2E6';
const FACT = '#22C55E';

type Period = 'day' | '10d' | 'month' | 'quarter' | 'year';
const PRESETS: { k: Period; label: string }[] = [
  { k: 'day', label: 'd_p_today' },
  { k: '10d', label: 'd_p_10d' },
  { k: 'month', label: 'd_p_month' },
  { k: 'quarter', label: 'd_p_quarter' },
  { k: 'year', label: 'd_p_year' },
];

function rangeOf(p: Period): [string, string] {
  const d = new Date();
  const iso = (x: Date) => x.toISOString().slice(0, 10);
  const to = iso(d);
  const s = new Date(d);
  if (p === '10d') s.setDate(d.getDate() - 9);
  else if (p === 'month') s.setDate(1);
  else if (p === 'quarter') { s.setMonth(Math.floor(d.getMonth() / 3) * 3, 1); }
  else if (p === 'year') s.setMonth(0, 1);
  return [iso(s), to];
}

interface DayRow { day: string; planMwh: number; factMwh: number; }
interface Series { totals: { planMwh: number; factMwh: number }; daily: DayRow[]; }

const fmtMwh = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)} ГВт·год` : `${Math.round(v)} ${t('d_u_mwh')}`);

export default function GenerationChart() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>('month');
  const [data, setData] = useState<Series | null>(null);
  const [loading, setLoading] = useState(true);

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const textDim = isDark ? '#4A4A50' : '#9B9BA4';
  const innerBg = isDark ? '#0F0F0F' : '#F5F5F7';
  const chipBg = isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F5';

  useEffect(() => {
    const [from, to] = rangeOf(period);
    setLoading(true);
    authFetch(`${API_BASE_URL}/owner/revenue-series?from=${from}&to=${to}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period]);

  const fmtMwhT = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)} ${t('d_u_gwh')}` : `${Math.round(v)} ${t('d_u_mwh')}`);
  const totals = data?.totals;
  const daily = (data?.daily ?? []).map((d) => ({ ...d, dd: d.day.slice(8) + '.' + d.day.slice(5, 7) }));
  const shortfall = totals ? Math.max(0, totals.planMwh - totals.factMwh) : 0;
  const fulfil = totals && totals.planMwh > 0 ? Math.round((totals.factMwh / totals.planMwh) * 100) : null;

  const Metric = ({ label, value, color }: { label: string; value: string; color: string }) => (
    <div className="rounded-lg px-3 py-2" style={{ background: innerBg }}>
      <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: textMuted }}>{label}</div>
      <div className="text-base font-bold tabular-nums" style={{ color, fontFamily: 'Muller, Inter, sans-serif', letterSpacing: '-0.02em' }}>{value}</div>
    </div>
  );

  return (
    <div className="rounded-xl p-5 mb-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
            {t('d_gen_title')}
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: textMuted }}>
            {t('d_gen_subtitle')}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.k}
              onClick={() => setPeriod(p.k)}
              className="px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
              style={{
                background: period === p.k ? '#D12229' : chipBg,
                color: period === p.k ? '#fff' : textMuted,
                border: `1px solid ${period === p.k ? '#D12229' : cardBorder}`,
                fontFamily: 'Muller, Inter, sans-serif',
              }}
            >{t(p.label)}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <Metric label={t('d_map_forecast')} value={totals ? fmtMwhT(totals.planMwh) : '—'} color={PLAN} />
        <Metric label={t('d_fact')} value={totals ? fmtMwhT(totals.factMwh) : '—'} color={FACT} />
        <Metric label={t('d_gen_shortfall')} value={totals ? fmtMwhT(shortfall) : '—'} color="#E0A030" />
        <Metric label={t('d_gen_fulfil')} value={fulfil != null ? `${fulfil}%` : '—'} color={textMain} />
      </div>

      <div style={{ height: 230 }}>
        {loading ? (
          <div className="flex items-center justify-center h-full text-xs" style={{ color: textMuted }}>{t('d_chart_loading')}</div>
        ) : daily.length === 0 ? (
          <div className="flex items-center justify-center h-full text-xs" style={{ color: textMuted }}>{t('d_chart_no_data')}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={daily} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={cardBorder} vertical={false} />
              <XAxis dataKey="dd" tick={{ fontSize: 9, fill: textDim }} axisLine={false} tickLine={false}
                     interval={Math.max(0, Math.floor(daily.length / 15))} />
              <YAxis tick={{ fontSize: 9, fill: textDim }} axisLine={false} tickLine={false} width={38} />
              <Tooltip
                contentStyle={{ background: innerBg, border: `1px solid ${cardBorder}`, borderRadius: 8, fontSize: 12, color: textMain }}
                formatter={(v: number, n: string) => [`${Math.round(v)} МВт·год`, n === 'planMwh' ? t('d_map_forecast') : t('d_fact')]}
                labelFormatter={(l: string) => `${l}`}
              />
              <Line type="monotone" dataKey="planMwh" name="planMwh" stroke={PLAN} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="factMwh" name="factMwh" stroke={FACT} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="flex items-center gap-4 mt-2 text-[11px]" style={{ color: textMuted }}>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PLAN }} /> {t('d_map_forecast')}</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: FACT }} /> {t('d_fact')}</span>
      </div>
    </div>
  );
}
